import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// --- Helpers ---
function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

function renderMd(text) {
  let s = escapeHtml(text);
  // Markdown links first: [label](https://...)
  s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  // Auto-link bare http(s) URLs that aren't already inside an <a>. Safety
  // net so wa.me/... URLs emitted as plain text by the LLM still open the
  // chat as a clickable link. The negative-lookbehind on `href="` prevents
  // double-wrapping links we already rewrote in the previous step.
  s = s.replace(/(^|[^"=>])(https?:\/\/[^\s<]+)/g, (_m, prefix, url) => {
    return `${prefix}<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
  s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/\n/g, '<br/>');
  return s;
}

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}>
    <path d="M20.52 3.48A11.78 11.78 0 0012.06 0C5.5 0 .15 5.34.15 11.91c0 2.1.55 4.15 1.6 5.96L0 24l6.3-1.65a11.93 11.93 0 005.75 1.46h.01c6.56 0 11.91-5.34 11.91-11.91 0-3.18-1.24-6.17-3.45-8.42zM12.06 21.8a9.86 9.86 0 01-5.03-1.38l-.36-.21-3.74.98 1-3.65-.23-.37a9.85 9.85 0 01-1.51-5.26c0-5.46 4.45-9.91 9.92-9.91 2.65 0 5.14 1.03 7.01 2.91a9.85 9.85 0 012.9 7.01c0 5.46-4.45 9.91-9.96 9.91zm5.45-7.42c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.27-.47-2.41-1.49-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.6-.92-2.19-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.22 3.08.15.2 2.1 3.21 5.08 4.5.71.31 1.27.49 1.7.62.71.23 1.36.2 1.87.12.57-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35z" />
  </svg>
);

export default function PartnerChatPage() {
  const { bot_slug } = useParams();
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const bodyRef = useRef(null);
  const textareaRef = useRef(null);
  const wrapperRef = useRef(null);

  // Ensure mobile viewport prevents iOS auto-zoom on input focus (font-size:16px
  // on the textarea is the primary fix, but we also pin the viewport scale).
  useEffect(() => {
    const existing = document.querySelector('meta[name="viewport"]');
    const previous = existing ? existing.getAttribute('content') : null;
    const tag = existing || document.createElement('meta');
    tag.name = 'viewport';
    tag.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
    if (!existing) document.head.appendChild(tag);
    return () => {
      if (existing && previous != null) tag.setAttribute('content', previous);
      else if (!existing) tag.remove();
    };
  }, []);

  // Lift the whole chat above the iOS soft keyboard. The visualViewport API
  // exposes the visible region; the gap between layout viewport and visual
  // viewport bottom is the keyboard height, which we offset on the wrapper.
  useEffect(() => {
    if (!window.visualViewport) return undefined;
    const adjust = () => {
      const vv = window.visualViewport;
      const gap = window.innerHeight - vv.offsetTop - vv.height;
      if (wrapperRef.current) wrapperRef.current.style.bottom = Math.max(0, gap) + 'px';
      if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    };
    window.visualViewport.addEventListener('resize', adjust);
    window.visualViewport.addEventListener('scroll', adjust);
    adjust();
    return () => {
      if (!window.visualViewport) return;
      window.visualViewport.removeEventListener('resize', adjust);
      window.visualViewport.removeEventListener('scroll', adjust);
    };
  }, []);

  // Load public config
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/partner/public/${bot_slug}`);
        if (!mounted) return;
        setConfig(r.data);
        const welcome = r.data.welcome_message || '¡Hola! ¿En qué te puedo ayudar?';
        setMessages([{ role: 'bot', text: welcome, showWA: false }]);
        document.title = r.data.business_name || 'Asistente';
      } catch (e) {
        if (!mounted) return;
        setError('Bot no encontrado');
      }
    })();
    return () => { mounted = false; };
  }, [bot_slug]);

  // Auto-scroll
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, isTyping]);

  const send = useCallback(async () => {
    const text = (input || '').trim();
    if (!text || sending || !config) return;
    setInput('');
    // Reset auto-grown height and dismiss the iOS soft keyboard.
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.blur();
    }
    const next = [...messages, { role: 'user', text, showWA: false }];
    setMessages(next);
    setSending(true);
    setIsTyping(true);
    try {
      const history = next.filter(m => !m.welcome).map(m => ({
        role: m.role === 'user' ? 'user' : 'assistant',
        content: m.text,
      }));
      const r = await axios.post(`${BACKEND_URL}/api/partner/chat`, {
        bot_slug: bot_slug,
        message: text,
        conversation_history: history.slice(-20),
      });
      const replyRaw = (r.data?.reply || '').trim();
      let showWA = false;
      let clean = replyRaw;
      if (/\[WA\]\s*$/.test(clean)) {
        showWA = true;
        clean = clean.replace(/\[WA\]\s*$/, '').trim();
      }
      setMessages((prev) => [...prev, { role: 'bot', text: clean, showWA }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'No pude conectar. Intenta de nuevo.';
      setMessages((prev) => [...prev, { role: 'bot', text: 'Lo siento, ' + detail, showWA: false }]);
    } finally {
      setIsTyping(false);
      setSending(false);
    }
  }, [input, sending, config, messages, bot_slug]);

  if (error) {
    return (
      <div style={{ fontFamily: 'system-ui, sans-serif', textAlign: 'center', padding: 40 }}>
        <h2>Bot no encontrado</h2>
        <p>El asistente que buscas no existe o fue eliminado.</p>
        <p><a href="https://acbothub.com" style={{ color: '#6366f1' }}>Volver a ACBotHub</a></p>
      </div>
    );
  }
  if (!config) {
    return <div style={{ padding: 40, textAlign: 'center', fontFamily: 'system-ui' }}>Cargando…</div>;
  }

  const brand = config.primary_color || '#1a1a2e';
  const textOnBrand = config.text_color || '#ffffff';
  const wa = (config.wa_number || '').replace(/\D/g, '');

  return (
    <div
      ref={wrapperRef}
      style={{
        // Fixed positioning so the layout doesn't fight against mobile address
        // bars. The visualViewport effect adjusts `bottom` when the keyboard
        // pops, pushing the entire chat up.
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', flexDirection: 'column',
        maxWidth: 520, margin: '0 auto', background: '#fff',
        fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif',
        boxShadow: '0 0 0 1px rgba(15,23,42,.06)', overflow: 'hidden',
      }}
      data-testid="partner-chat-page"
    >
      <style>{`
        @keyframes pulse-dot{0%{box-shadow:0 0 0 0 rgba(34,197,94,.5)}70%{box-shadow:0 0 0 10px rgba(34,197,94,0)}100%{box-shadow:0 0 0 0 rgba(34,197,94,0)}}
        @keyframes bounce-dot{0%,80%,100%{transform:translateY(0);opacity:.4}40%{transform:translateY(-5px);opacity:1}}
        .pc-bubble a{color:#2563eb;text-decoration:underline;}
        .pc-bubble-user a{color:#dbeafe;}
      `}</style>

      {/* Header */}
      <header style={{
        flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
        background: brand, color: textOnBrand,
        position: 'relative', zIndex: 10,
      }}>
        {config.logo_url ? (
          <img src={config.logo_url} alt={config.business_name}
            onError={(e) => { e.target.style.display = 'none'; }}
            style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', background: '#fff' }} />
        ) : (
          <div style={{
            width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16,
          }}>{(config.business_name || 'A').charAt(0).toUpperCase()}</div>
        )}
        <div>
          <div style={{ fontWeight: 600, fontSize: 15, lineHeight: 1.2 }} data-testid="chat-business-name">
            {config.business_name}
          </div>
          <div style={{ fontSize: 11, opacity: .8, display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%', background: '#22c55e',
              animation: 'pulse-dot 2s infinite',
            }} />
            <span>En línea</span>
          </div>
        </div>
      </header>

      {/* Body */}
      <div ref={bodyRef} style={{
        flex: '1 1 0', minHeight: 0, overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        padding: 16, background: '#f8fafc',
      }} data-testid="chat-body">
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', marginBottom: 10, justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div
              className={`pc-bubble ${m.role === 'user' ? 'pc-bubble-user' : ''}`}
              data-testid={m.role === 'user' ? 'user-msg' : 'bot-msg'}
              style={{
                maxWidth: '78%', padding: '10px 14px', borderRadius: 16,
                fontSize: 14.5, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordWrap: 'break-word',
                background: m.role === 'user' ? brand : '#fff',
                color: m.role === 'user' ? textOnBrand : '#111827',
                border: m.role === 'user' ? 'none' : '1px solid #e5e7eb',
                borderBottomRightRadius: m.role === 'user' ? 4 : 16,
                borderBottomLeftRadius: m.role === 'user' ? 16 : 4,
              }}
            >
              <span dangerouslySetInnerHTML={{ __html: renderMd(m.text) }} />
              {m.showWA && wa && (
                <div style={{ marginTop: 8 }}>
                  <a
                    href={`https://wa.me/${wa}?text=${encodeURIComponent('Hola, quiero hablar con un ejecutivo')}`}
                    target="_blank" rel="noopener noreferrer"
                    onClick={() => {
                      // Fire-and-forget: track the lead conversion. Never block redirect.
                      try {
                        axios.post(`${BACKEND_URL}/api/partner/track/wa-click`, { bot_slug }).catch(() => {});
                      } catch (e) { /* noop */ }
                    }}
                    data-testid="wa-cta-btn"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      background: '#22c55e', color: '#fff', textDecoration: 'none', fontWeight: 600,
                      padding: '10px 16px', borderRadius: 24, fontSize: 14,
                      boxShadow: '0 6px 14px rgba(34,197,94,.35)',
                    }}
                  >
                    <WhatsAppIcon /> Hablar con un ejecutivo
                  </a>
                </div>
              )}
            </div>
          </div>
        ))}
        {isTyping && (
          <div style={{ display: 'flex', marginBottom: 10 }}>
            <div style={{
              padding: '10px 14px', borderRadius: 16, background: '#fff', border: '1px solid #e5e7eb',
              borderBottomLeftRadius: 4,
            }}>
              <div style={{ display: 'inline-flex', gap: 4 }}>
                {[0, 1, 2].map(i => (
                  <span key={i} style={{
                    width: 7, height: 7, background: '#94a3b8', borderRadius: '50%',
                    animation: `bounce-dot 1.2s infinite`, animationDelay: `${i * 0.15}s`,
                  }} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer — branding, sits just above the composer */}
      <div style={{
        flexShrink: 0,
        textAlign: 'center', fontSize: 11, color: '#94a3b8',
        padding: '6px 0 4px', background: '#fff',
      }}>
        Powered by{' '}
        <a
          href="https://acbothub.com"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="powered-by-link"
          style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 500 }}
        >
          ACBotHub
        </a>
      </div>

      {/* Composer — fixed at bottom, never scrolls out of view */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        style={{
          flexShrink: 0,
          display: 'flex', alignItems: 'flex-end', gap: 10,
          padding: '10px 14px max(10px, env(safe-area-inset-bottom, 10px))',
          background: '#fff', borderTop: '1px solid #e5e7eb',
          position: 'relative', zIndex: 10,
        }}
        autoComplete="off"
      >
        <textarea
          ref={textareaRef}
          rows={1}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            // Auto-grow up to 100px, then scroll inside the textarea.
            const el = e.target;
            el.style.height = 'auto';
            el.style.height = Math.min(el.scrollHeight, 100) + 'px';
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Escribe tu mensaje..."
          data-testid="chat-input"
          maxLength={2000}
          autoComplete="on"
          autoCorrect="on"
          autoCapitalize="sentences"
          spellCheck="true"
          enterKeyHint="send"
          style={{
            flex: 1, minWidth: 0,
            // 16px is the minimum font-size that prevents iOS Safari from
            // auto-zooming the page when the textarea receives focus.
            fontSize: 16, lineHeight: 1.4, fontFamily: 'inherit',
            padding: '11px 16px',
            border: '1px solid #e5e7eb', borderRadius: 22,
            background: '#f8fafc', color: '#111827',
            outline: 'none', resize: 'none',
            maxHeight: 100, overflowY: 'auto',
            WebkitAppearance: 'none',
            transition: 'border-color 0.2s',
          }}
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          aria-label="Enviar"
          data-testid="chat-send-btn"
          style={{
            flexShrink: 0,
            width: 40, height: 40, borderRadius: '50%',
            background: brand, color: textOnBrand,
            border: 0, padding: 0, marginBottom: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: sending || !input.trim() ? 'not-allowed' : 'pointer',
            opacity: sending || !input.trim() ? 0.5 : 1,
            boxShadow: '0 2px 10px rgba(15,23,42,.18)',
            WebkitTapHighlightColor: 'transparent',
            transition: 'transform 0.15s, opacity 0.2s',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={textOnBrand} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </form>
    </div>
  );
}
